import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { RootState } from '@/redux/store';
import { useCurpExtraction } from '@/features/onboarding/hooks/useCurpExtraction';
import { useOnboarding } from '@/features/onboarding/hooks/useOnboarding';
import { getMyPlanRecommendation } from '@/shared/services/planRecommendation.service';
import { showAlert } from '@/redux/slices/alert.slice';
import { ALERT_TYPE } from '@/components/molecules/Alert';
import useEvents from '@/hooks/useEvents';
import { EVENT_TYPES } from '@/shared/constants/events';
import type { PlanFamily } from './catalog';
import {
	planMismatchMessage,
	toPlanRegimeFit,
	type PlanRegimeFit,
} from './planRegimeFit';

export const PLAN_RECOMMENDATION_QUERY_KEY = ['plan-recommendation', 'me'] as const;

/** Las respuestas del onboarding que mueven la recomendación (régimen declarado, ocupación, aliado, CURP). */
const SIGNAL_ANSWER_KEYS = ['regimen', 'occupation', 'ally_ids', 'curp'] as const;

/**
 * EL plan recomendado del usuario en sesión, como lo resuelve el back (ver
 * planRegimeFit): la única lectura de toda la web. Se vuelve a pedir solo
 * cuando cambia algo que lo mueve: las respuestas del onboarding que usa, la
 * consulta por CURP que termina o su vinculación con el SAT. Mientras cambia se
 * queda el plan anterior; `isLoading` solo es la primera carga, para que las
 * superficies no pinten (ni cuenten) una experiencia sin plan que no es.
 */
export const usePlanRegimeFitQuery = (): { fit: PlanRegimeFit; isLoading: boolean } => {
	const userId = useSelector((state: RootState) => state.profile.user?.id);
	const taxpayer = useSelector((state: RootState) => state.taxProfile.taxpayer);
	const { state: curpExtraction } = useCurpExtraction();
	const { data: onboarding } = useOnboarding();
	const answers = onboarding?.answers;
	const inputs = JSON.stringify([
		SIGNAL_ANSWER_KEYS.map((key) => answers?.[key]?.value ?? null),
		curpExtraction?.status ?? null,
		taxpayer?.id ?? null,
		taxpayer?.tax_status?.regimes?.length ?? null,
	]);
	const { data, isPending } = useQuery({
		queryKey: [...PLAN_RECOMMENDATION_QUERY_KEY, inputs],
		queryFn: getMyPlanRecommendation,
		enabled: !!userId,
		staleTime: Infinity,
		placeholderData: keepPreviousData,
	});
	const fit = useMemo(() => toPlanRegimeFit(data), [data]);
	return { fit, isLoading: !!userId && isPending };
};

export const usePlanRegimeFit = (): PlanRegimeFit => usePlanRegimeFitQuery().fit;

export type PlanRegimeMismatchAction =
	| 'warned'
	| 'continued'
	| 'switched_to_recommended'
	/** The user already has an active plan and chose to keep it. */
	| 'kept_current'
	| 'dismissed';

/**
 * Records that someone picked a plan that does not cover their regime, and what
 * they did next. A single event for all surfaces: `surface` tells them apart.
 * `plan_family` is the RECOMMENDED plan and the event carries
 * `recommendation_arm: 'guided'` like the other recommended-plan events; the plan
 * they picked goes in `chosen_plan_family`.
 */
export const usePlanRegimeMismatchTracking = () => {
	const fit = usePlanRegimeFit();
	const { trackEvent } = useEvents();

	return useCallback(
		(family: PlanFamily, surface: string, action: PlanRegimeMismatchAction) =>
			trackEvent({
				eventName: EVENT_TYPES.PLAN_REGIME_MISMATCH,
				eventPayload: {
					surface,
					action,
					chosen_plan_family: family,
					plan_family: fit.recommended,
					recommendation_arm: 'guided',
					regimes: fit.regimes.join(','),
					regime_source: fit.source,
				},
			}),
		[fit, trackEvent],
	);
};

/**
 * Aviso para las superficies que meten un plan al carrito sin pasar por
 * /planes (deeplinks, upsell, home, onboarding): no frena la compra, solo le
 * dice al usuario que ese plan no cubre su régimen. Devuelve si avisó.
 */
export const usePlanRegimeWarning = () => {
	const fit = usePlanRegimeFit();
	const dispatch = useDispatch();
	const trackMismatch = usePlanRegimeMismatchTracking();

	return useCallback(
		(family: PlanFamily, surface: string): boolean => {
			if (fit.covers(family)) return false;
			trackMismatch(family, surface, 'warned');
			dispatch(
				showAlert({
					type: ALERT_TYPE.WARNING,
					title: 'Este plan no es el recomendado para ti',
					description: planMismatchMessage(fit),
				}),
			);
			return true;
		},
		[fit, dispatch, trackMismatch],
	);
};
